import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type IEbayMarketplace } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Divider, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  ebayCaregoryLoadAspects,
  ebayMarketplaceLoad,
  ebayMarketplaceLoadFulfillmentPolicies,
  ebayMarketplaceLoadPaymentPolicies,
  ebayMarketplaceLoadReturnPolicies,
  ebayMarketplacesLoad,
  ebayProductLoad,
} from '../../reducers/ebay/reducer';
import Aspect from './ebay/aspect';
import Inventories from './ebay/inventories';
import Marketplace from './ebay/marketplace';
import Offers from './ebay/offers';

const { Title } = Typography;

function EbayProductContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const ebayMarketplaces = useSelector((s: RootState) => s.Ebay.marketplaces);
  const ebayMarketplacesTimestamp = useSelector(
    (s: RootState) => s.Ebay.marketplacesTimestamp,
  );
  const productUUID = useSelector((s: RootState) => s.Product.uuid);

  const [marketplaces, setMarketplaces] = useState<IEbayMarketplace[]>([]);
  const [marketplaceElements, setMarketplaceElements] = useState<
    React.ReactElement[]
  >([]);
  const [aspectElements, setAspectElements] = useState<React.ReactElement[]>(
    [],
  );

  useEffect(() => {
    ebayMarketplaces.forEach((marketplace) => {
      if (marketplace.isActive) {
        dispatch(ebayMarketplaceLoad(marketplace.code));
        dispatch(ebayMarketplaceLoadFulfillmentPolicies(marketplace.code));
        dispatch(ebayMarketplaceLoadPaymentPolicies(marketplace.code));
        dispatch(ebayMarketplaceLoadReturnPolicies(marketplace.code));
        dispatch(ebayProductLoad(productUUID, marketplace.code));
        dispatch(ebayCaregoryLoadAspects(productUUID, marketplace.code));
      }
    });
  }, [dispatch, ebayMarketplacesTimestamp, ebayMarketplaces, productUUID]);

  useEffect(() => {
    const marketplacesUE = ebayMarketplaces
      .filter(({ isActive }) => isActive)
      .sort((a, b) => stringCompare(a.name, b.name));

    const marketplaceElementsUE = marketplacesUE.map((marketplace) => (
      <Marketplace Marketplace={marketplace} key={marketplace.code} />
    ));

    const aspectElementsUE = marketplacesUE.map((marketplace) => (
      <Aspect Marketplace={marketplace} key={marketplace.code} />
    ));

    setMarketplaces(marketplacesUE);
    setMarketplaceElements(marketplaceElementsUE);
    setAspectElements(aspectElementsUE);
  }, [ebayMarketplacesTimestamp, ebayMarketplaces]);

  useEffect((): void => {
    dispatch(ebayMarketplacesLoad());
  }, [dispatch, productUUID]);

  return (
    <>
      <Title level={4} style={{ margin: 0 }}>
        eBay
      </Title>

      <Title level={3}>Marketplaces</Title>

      {marketplaceElements.map((marketplaceElement) => marketplaceElement)}

      <Divider />
      <Title level={3}>Aspects</Title>

      {aspectElements.map((aspectElement) => aspectElement)}

      <Divider />
      <Title level={3}>Inventory</Title>

      <Inventories Marketplaces={marketplaces} />

      <Divider />
      <Title level={3}>Offer</Title>

      <Offers Marketplaces={marketplaces} />
    </>
  );
}

export default React.memo(EbayProductContainer);
